import React, { useState } from "react";
import { Button, List, Alert } from "@theorgchartdev/storybook";
import SectionCard, {
  SectionCardGroup
} from "@/appComponents/Blocks/Composites/SectionCard/SectionCard";
import { BuildSecurityList, HandleCopyProfile, getProfileNameError } from "./SecurityUtils";
import { RowLevelSecurityModal } from "@/appComponents/Blocks/Modals/SettingsModal/AccountSettings/Security/RowLevelSecurity/RowLevelSecurityModal";
import { BranchLevelSecurityModal } from "@/appComponents/Blocks/Modals/SettingsModal/AccountSettings/Security/BranchLevelSecurity/BranchLevelSecurityModal";
import { appstr } from "@coreapp/utils/appstr";
import { enumFeaturesList, kAccountPremium } from "@coreapp/constants";
import { Globals } from "@coreapp/CoreApp/Globals";
import { getFeatureAccess } from "@/Utils/Misc";
import UpgradePopover from "@/appComponents/Blocks/Composites/UpgradePopover/UpgradePopover";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import UpgradeCard from "@/appComponents/Blocks/Composites/UpgradePopover/UpgradeCard";
import { UpgradeInfoCard } from "@/appComponents/Blocks/Composites/UpgradePopover/UpgradeInfoCard";

export const GetNewProfile = (securityConfig, key) => {
  let newProfileID = "0";
  let defaultName = appstr("Untitled");
  let existingIDs = [];
  let existingNames = [];

  let config = securityConfig[key] ?? {};

  for (var anID in config) {
    var theObj = config[anID];
    existingIDs.push(anID);
    let theName = theObj["name"] ? theObj["name"] : anID;
    existingNames.push(theName);
  }

  while (existingIDs.indexOf(newProfileID) > -1)
    newProfileID = (parseInt(newProfileID) + 1).toString();

  var counter = 1;

  var newName = defaultName;
  while (existingNames.includes(newName)) {
    newName = defaultName + " (" + counter.toString() + ")";
    counter++;
  }

  let newProfile = {
    name: newName
  };

  if (key === "rowlevel") {
    newProfile.allowedFields = [];
    newProfile.restrictedFields = [];
  }

  return { newProfile, newProfileID };
};

export const SecurityConfiguration = ({ accountConfig, handleUpdate }) => {
  const [editingProfileMgr, setEditingProfileMgr] = useState(null);

  const [showRLSModal, setShowRLSModal] = useState(false);
  const [editRLSProfile, setEditRLSProfile] = useState(null);
  const [newRLSMode, setNewRLSMode] = useState(false);

  const [showBLSModal, setShowBLSModal] = useState(false);
  const [editBLSProfile, setEditBLSProfile] = useState(null);
  const [newBLSMode, setNewBLSMode] = useState(false);

  if (!accountConfig) return;

  const securityObj = accountConfig.security;
  const rowLevelProfiles = securityObj.rowlevel ?? {};
  const branchLevelProfiles = securityObj.branchlevel ?? {};

  let currentEdition = Globals._accountInfo.GetOC5Edition();
  let hasAdvSec = currentEdition == kAccountPremium;

  const { showPricingAndPackaging } = usePermissionCheck();

  const getRenameValidation = (value, profileType, profileID) => {
    const error = getProfileNameError(
      accountConfig.security,
      value,
      `${profileType}level`,
      String(profileID)
    );
    return {
      result: !error,
      msg: error,
      status: error ? "danger" : undefined,
      showText: !!error
    };
  };

  const HandleRenameSave = (value, eventInfo) => {
    const name = (value ?? "").trim();
    if (getRenameValidation(name, eventInfo.type, eventInfo.id).result) {
      let updatedProfiles =
        eventInfo.type === "row" ? { ...rowLevelProfiles } : { ...branchLevelProfiles };
      const updateKey = `${eventInfo.type}level`;
      updatedProfiles[eventInfo.id]["name"] = name;

      const updatedAccountConfig = {
        ...accountConfig,
        security: { ...accountConfig.security, [updateKey]: updatedProfiles }
      };

      handleUpdate(updatedAccountConfig, "security");
      setEditingProfileMgr(null);
    }
  };

  const HandleProfileChange = (action, profileType, profileID) => {
    let updatedProfiles =
      profileType === "row" ? { ...rowLevelProfiles } : { ...branchLevelProfiles };
    const updateKey = `${profileType}level`;
    switch (action) {
      case "edit":
        //open modal here
        if (profileType === "row") {
          setShowRLSModal(true);
          setEditRLSProfile(profileID);
        } else if (profileType === "branch") {
          setShowBLSModal(true);
          setEditBLSProfile(profileID);
        }
        // return without dirtying state
        return;
      case "copy":
        updatedProfiles = HandleCopyProfile(updatedProfiles, profileID);
        break;
      case "delete":
        delete updatedProfiles[profileID];
        break;
      default:
        break;
    }
    const updatedAccountConfig = {
      ...accountConfig,
      security: { ...accountConfig.security, [updateKey]: updatedProfiles }
    };

    handleUpdate(updatedAccountConfig, "security");
  };

  const closeRLSModal = () => {
    setShowRLSModal(false);
    setEditRLSProfile(null);
    setNewRLSMode(false);
  };

  const createNewRLSProfile = () => {
    setNewRLSMode(true);
    setShowRLSModal(true);
    let { newProfileID } = GetNewProfile(securityObj, "rowlevel");
    setEditRLSProfile(newProfileID);
  };

  const addRLSProfileBtn = (
    <Button
      modifier={"secondary"}
      className={"oc-special-secondary"}
      dataTestId="account-settings-security-create-rls"
      iconProps={{ name: "plus" }}
      onClick={() => createNewRLSProfile("rowlevel")}
      disabled={!getFeatureAccess(enumFeaturesList.RLS)}
    >
      {`${appstr("Create")} ${appstr("Security Profile")}`}
    </Button>
  );

  const closeBLSModal = (updateRLS, rlsProfile) => {
    setShowBLSModal(false);
    setEditBLSProfile(null);
    setNewBLSMode(false);
  };

  const createNewBLSProfile = () => {
    setNewBLSMode(true);
    setShowBLSModal(true);
    let { newProfileID } = GetNewProfile(securityObj, "branchlevel");
    setEditBLSProfile(newProfileID);
  };

  const addBLSProfileBtn = (
    <Button
      modifier={"secondary"}
      className={"oc-special-secondary"}
      dataTestId="account-settings-security-create-bls-btn"
      iconProps={{ name: "plus" }}
      onClick={() => createNewBLSProfile("branchlevel")}
      disabled={!getFeatureAccess(enumFeaturesList.BLS)}
    >
      {`${appstr("Create")} ${appstr("Security Profile")}`}
    </Button>
  );

  const upgradeBLSCheck = () => {
    if (showPricingAndPackaging) {
      return getFeatureAccess(enumFeaturesList.BLS) ? addBLSProfileBtn : null;
    }
    return addBLSProfileBtn;
  };

  let customCardStyle = {};
  if (showPricingAndPackaging && !getFeatureAccess(enumFeaturesList.BLS)) {
    customCardStyle = { background: "var(--bg-brand-secondary)" };
  }

  let blsUpgradeMessageDynamicText = {
    template: appstr("bls upgrade template"),
    formatConfig: {
      bold1: { type: "bold" }
    },
    replaceDict: {
      bold1: appstr("branch level")
    }
  };

  return (
    <SectionCardGroup>
      {(editRLSProfile != null || newRLSMode) && (
        <RowLevelSecurityModal
          accountConfig={accountConfig}
          profiles={rowLevelProfiles}
          show={showRLSModal}
          existingProfileID={editRLSProfile}
          isCreateNewProfile={newRLSMode}
          onClose={closeRLSModal}
        />
      )}
      {hasAdvSec && (
        <Alert severity={"warning"} withBorder showIcon>
          {appstr("AdvSecEnterperisePaywall")}
        </Alert>
      )}
      <SectionCard title={appstr("Row Level Security")} titleContent={addRLSProfileBtn}>
        <List
          enableVirtualization={false}
          options={BuildSecurityList(
            rowLevelProfiles,
            HandleProfileChange,
            "row",
            HandleRenameSave,
            !getFeatureAccess(enumFeaturesList.RLS),
            getRenameValidation
          )}
          listWidth="fill"
          optionSize={"large"}
          showCheck={false}
          value={null}
        />
      </SectionCard>
      {(editBLSProfile != null || newBLSMode) && (
        <BranchLevelSecurityModal
          accountConfig={accountConfig}
          profiles={branchLevelProfiles}
          show={showBLSModal}
          existingProfileID={editBLSProfile}
          isCreateNewProfile={newBLSMode}
          onClose={closeBLSModal}
        />
      )}
      <SectionCard
        title={appstr("Branch Level Security")}
        titleContent={upgradeBLSCheck()}
        customCardStyle={customCardStyle}
      >
        {showPricingAndPackaging && !getFeatureAccess(enumFeaturesList.BLS) && (
          <UpgradeCard iconName={"user-lock-regular"} featureKey={enumFeaturesList.BLS}>
            <UpgradeInfoCard.Body dynamic={blsUpgradeMessageDynamicText}>
              {appstr("bls upgrade message")}
            </UpgradeInfoCard.Body>
          </UpgradeCard>
        )}
        {getFeatureAccess(enumFeaturesList.BLS) && (
          <List
            enableVirtualization={false}
            options={BuildSecurityList(
              branchLevelProfiles,
              HandleProfileChange,
              "branch",
              HandleRenameSave,
              !getFeatureAccess(enumFeaturesList.BLS),
              getRenameValidation
            )}
            listWidth="fill"
            optionSize={"large"}
            showCheck={false}
            value={null}
          />
        )}
      </SectionCard>
    </SectionCardGroup>
  );
};
